import {useEffect, useState} from 'react';
import {format} from 'date-fns';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {toast} from '@common-web/hooks/use-toast';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import type {GroupPipelineDetail} from '@internal/pages/group-pipeline/components/pipelineDisplay';

export function AddTaskDialog({
  vbaGroupId,
  pipeline,
  open,
  onOpenChange,
}: {
  vbaGroupId: string;
  pipeline: GroupPipelineDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [label, setLabel] = useState('');
  const [afterTaskId, setAfterTaskId] = useState('');
  const [dueDate, setDueDate] = useState('');

  const add = trpc.internal.groupPipeline.task.add.useMutation({
    onSuccess: () => {
      onOpenChange(false);
      toast({title: 'Task added'});
      return Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupPipeline.boardColumn.invalidate(),
      ]);
    },
    onError: createMutationErrorHandler('Could not add the task'),
  });

  const defaultAfterId = pipeline.state.currentTask?.id ?? pipeline.tasks.at(-1)?.id ?? '';
  useEffect(() => {
    if (!open) return;
    setLabel('');
    setDueDate('');
    // Default to just after the step the group is at.
    setAfterTaskId(defaultAfterId);
  }, [open, defaultAfterId]);

  const sections = [...new Set(pipeline.tasks.map((t) => t.section))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='grid-cols-[minmax(0,1fr)] sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Add task</DialogTitle>
          <DialogDescription>
            A one-off task for this group. It opens now, and the group can&apos;t go live until
            it&apos;s done or skipped.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='add-task-label'>Name</Label>
            <Input
              id='add-task-label'
              autoFocus
              value={label}
              maxLength={120}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='add-task-after'>Place after</Label>
            <Select value={afterTaskId} onValueChange={setAfterTaskId}>
              <SelectTrigger id='add-task-after' className='bg-white'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sections.map((section) => (
                  <SelectGroup key={section}>
                    <SelectLabel>{section}</SelectLabel>
                    {pipeline.tasks
                      .filter((t) => t.section === section)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='add-task-due'>Due date (optional)</Label>
            <Input
              id='add-task-due'
              type='date'
              value={dueDate}
              min={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!label.trim() || !afterTaskId || add.isPending}
            onClick={() =>
              add.mutate({
                afterTaskId,
                label: label.trim(),
                // Noon local time keeps the picked day from shifting across time zones.
                dueAt: dueDate ? new Date(`${dueDate}T12:00:00`) : undefined,
              })
            }
          >
            Add task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
